import {z} from 'zod';

import {getCourseDocumentPath} from './course';

// ============================================================================
// Path segments
// ============================================================================

export const COURSE_DOCS_SUBCOLLECTION = 'docs' as const;
export const COURSE_ZOOMS_DOCUMENT_ID = 'courseZooms' as const;

// ============================================================================
// Schemas
// ============================================================================

/**
 * A single Zoom account used to host a section meeting.
 */
export const ZoomAccountSchema = z.object({
  /** The Zoom account id. Looks like '0XNKGNyTT3MdlBwDnM8Wrg'. */
  zoomId: z.string(),
  /** The email of the Zoom account. Looks like 'codeinplace29@outlook.com'. */
  email: z.string(),
});

export type ZoomAccount = z.infer<typeof ZoomAccountSchema>;

/**
 * Stores the Zoom accounts used by a course.
 */
export const CourseZoomsDocumentSchema = z
  .object({
    /** The Zoom accounts used to hold section meetings. */
    sectionZoomAccounts: z.array(ZoomAccountSchema),
    /**
     * Additional Zoom accounts in use by other courses at a given section
     * `timeIndex`. Keyed by `timeIndex` serialized as a string (may be
     * negative). Used by the admin Zoom accounts editor to project peak
     * concurrent Zoom demand across overlapping time slots. Only time indexes
     * with a non-zero bump should be persisted.
     */
    timeIndexBumps: z.record(z.string(), z.number()).optional(),
  })
  .loose();

export type CourseZoomsDocument = z.infer<typeof CourseZoomsDocumentSchema>;

// ============================================================================
// Paths
// ============================================================================

export function getCourseZoomsDocumentPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `${getCourseDocumentPath({courseId})}/${COURSE_DOCS_SUBCOLLECTION}/${COURSE_ZOOMS_DOCUMENT_ID}`;
}
